// The fasting dial. Phase 1 of docs/plan-fasting-groups.md.
//
// A ring that "grows bigger, thicker, and darker red the longer the fast
// continues", which is the plan's own description and turns out to be three
// separate mappings rather than one: radius, stroke width, and hue. All three
// come off `intensity` from `fastDial()` in shared/, measured against a fixed 72
// hours -- NOT against the faster's own target -- so that two dials side by side
// in the group view are actually comparable. A 16-hour faster at their goal
// must not look as far along as somebody three days in.
//
// ⚠ INLINE SVG, NOT A CANVAS OR A LIBRARY. The dial has to render inside a grid
// cell at any size, print, survive a screenshot, and scale with the viewer's
// font size — and it is two circles and a text node. A canvas would need a
// device-pixel-ratio dance and a resize observer to do worse.
//
// ⚠ AND IT TICKS LOCALLY, which is why this feature needs no realtime at all.
// Elapsed hours are derived from `startedAt` and `Date.now()`, so every dial on
// the page advances on its own with no server round trip. The only things that
// need pushing are somebody STARTING or ENDING a fast, which happen once or
// twice a day — a modest poll covers that, and a WebSocket frame would be
// machinery for an event that fires twice. Compare tournaments, where a round
// change has to reach a room in seconds.

import { el } from '../dom.js';
import {
  fastDial,
  formatElapsed,
  type FastSession,
  type KetosisLevel,
} from '@msc/shared';

/** Geometry, in the SVG's own 100x100 user space. */
const CENTRE = 50;
const MIN_RADIUS = 30;
const MAX_RADIUS = 42;
const MIN_STROKE = 5;
const MAX_STROKE = 14;

export interface DialOptions {
  session: FastSession;
  nowMs: number;
  /** Big for the viewer's own dial, small for a grid cell. */
  size: 'large' | 'small';
}

/**
 * Where a fast's ring sits on the colour ramp.
 *
 * ⚠ NOT a CSS gradient or a hue rotation, and not because those are hard. The
 * plan says "darker red the longer the fast continues", and a naive
 * `hsl(0, 80%, L%)` walk from light to dark red passes through colours that
 * fail contrast against both themes at the light end and read as brown at the
 * dark end. Three named stops, each checked, with the ramp interpolating
 * between them in oklab -- which keeps the perceived lightness moving evenly
 * rather than bunching up in the middle the way sRGB interpolation does.
 *
 * The early stops are deliberately NOT red. An hour into a fast is not an
 * achievement and a red ring says "warning" as readily as it says "progress";
 * starting cool and warming up is what makes the red at the end mean something.
 */
function ringColour(intensity: number): string {
  // Stops at 0, 0.33 (~24h) and 1 (72h).
  if (intensity <= 0.33) {
    const t = intensity / 0.33;
    return `color-mix(in oklab, var(--dial-early) ${Math.round((1 - t) * 100)}%, var(--dial-mid))`;
  }
  const t = (intensity - 0.33) / 0.67;
  return `color-mix(in oklab, var(--dial-mid) ${Math.round((1 - t) * 100)}%, var(--dial-late))`;
}

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const node = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

/**
 * One member's dial.
 *
 * Returns a whole figure -- ring, elapsed time, name, stage -- because the group
 * view and the personal view want the same thing at two sizes, and splitting it
 * would mean two copies of the geometry.
 */
export function fastDialFigure(opts: DialOptions): HTMLElement {
  const { session, nowMs, size } = opts;
  const startedMs = Date.parse(`${session.startedAt.replace(' ', 'T')}Z`);
  const dial = fastDial(
    (nowMs - startedMs) / 3_600_000,
    session.targetHours,
    session.startingKetosis,
  );

  const radius = MIN_RADIUS + (MAX_RADIUS - MIN_RADIUS) * dial.intensity;
  const stroke = MIN_STROKE + (MAX_STROKE - MIN_STROKE) * dial.intensity;
  const circumference = 2 * Math.PI * radius;
  const colour = ringColour(dial.intensity);

  const svg = svgEl('svg', {
    viewBox: '0 0 100 100',
    class: `dial dial-${size}`,
    // ⚠ The ring is decorative to a screen reader: every number in it is also
    // in the text below, so announcing the SVG would read the same fast twice.
    'aria-hidden': 'true',
    focusable: 'false',
  });

  svg.append(
    svgEl('circle', {
      cx: CENTRE,
      cy: CENTRE,
      r: radius,
      fill: 'none',
      stroke: 'var(--dial-track)',
      'stroke-width': stroke,
    }),
  );

  const arc = svgEl('circle', {
    cx: CENTRE,
    cy: CENTRE,
    r: radius,
    fill: 'none',
    stroke: colour,
    'stroke-width': stroke,
    'stroke-linecap': 'round',
    'stroke-dasharray': circumference,
    // Drawn clockwise from twelve o'clock, which is where a person expects a
    // clock to start. The rotation is on the element rather than the path so the
    // dash offset arithmetic stays readable.
    'stroke-dashoffset': circumference * (1 - dial.progress),
    transform: `rotate(-90 ${CENTRE} ${CENTRE})`,
  });
  svg.append(arc);

  const elapsed = formatElapsed(dial.hours);
  const ring = el('div', { class: 'dial-wrap' }, [
    svg as unknown as HTMLElement,
    el('div', { class: 'dial-centre' }, [
      el('span', { class: 'dial-hours' }, elapsed),
      ...(session.targetHours !== null
        ? [
            el(
              'span',
              { class: 'dial-target' },
              dial.targetMet ? `${session.targetHours}h target met` : `of ${session.targetHours}h`,
            ),
          ]
        : []),
    ]),
  ]);

  // Built as statements rather than a nest of spread ternaries. The first draft
  // was one expression three levels deep and did not even parse; a caption made
  // of four optional lines is exactly the shape that should be a list you push
  // onto.
  const caption: HTMLElement[] = [
    el('p', { class: 'dial-name' }, session.isMe ? 'You' : session.displayName),
    el('p', { class: 'dial-stage' }, dial.stage.label),
  ];

  if (size === 'large') {
    caption.push(el('p', { class: 'muted small' }, dial.stage.detail));
    if (session.startingKetosis === 'moderate' || session.startingKetosis === 'deep') {
      // Said out loud rather than applied silently: the timeline is shifted for
      // somebody who started already fat adapted, and a stage arriving "early"
      // with no explanation looks like a bug.
      caption.push(
        el(
          'p',
          { class: 'muted small' },
          'These stages are brought forward because you logged ketosis before starting. ' +
            'They are a rough guide either way.',
        ),
      );
    }
  } else {
    const badge = ketosisBadge(session.latestKetosis);
    if (badge) caption.push(badge);
  }

  return el('figure', { class: `dial-figure dial-figure-${size}` }, [
    ring,
    el('figcaption', {}, caption),
  ]);
}

/**
 * The group view's per-member ketosis badge.
 *
 * ⚠ The CURRENT level only, never a history. A member's readings over time are
 * a health record and stay theirs (`listMyKetosis` is scoped to the caller);
 * the plan asks for the group to see where everybody is now, which is one word.
 */
function ketosisBadge(level: KetosisLevel | null): HTMLElement | null {
  if (level === null || level === 'none') return null;
  return el('p', {}, [
    el('span', { class: `badge ketosis-${level}` }, level === 'deep' ? 'Deep ketosis' : 'In ketosis'),
  ]);
}
